/**
 * The Mapping tab's Map: the loan tape's lineage as a graph, in the style of
 * the one that opens from a chat answer.
 *
 * Left to right, one row per standardized table: the raw tabs that fill it,
 * the table, the join that attaches it to the loan, and the tape. Where the
 * run recorded the example file the tape was set up on, that file's tabs sit
 * beside the new file's, reaching the same tables: one schema, two files.
 *
 * The canvas is lazy: it brings the graph library with it, and a tape opened
 * to read or download should not pay for that.
 */

import { X } from "lucide-react";
import React, { Suspense, lazy, useMemo, useState } from "react";

import type { LoanTapePreviewColumn, SchemaGraphBlock } from "../Chat/Chat.types";
import { ORIGINS, ORIGIN_LABEL } from "./loanTapeColumns";
import {
	buildTapeMap,
	tableLabel,
	type TapeMap,
	type TapeMapKind,
	type TapeMapNode,
} from "./tapeMapModel";
import "../ContextGraph/contextGraph.css";
import "./tapeMapGraph.css";

const TapeMapCanvas = lazy(() => import("./TapeMapCanvas"));

export interface TapeMapGraphProps {
	graph: SchemaGraphBlock;
	columns: readonly LoanTapePreviewColumn[];
	/** How many loans the tape has, when the run says. */
	loans: number | null;
	/** The new file's name, for the legend and the panel. */
	sourceName?: string | null;
	/** The example file's name. */
	exampleName?: string | null;
	/** The deal each file sits in: what its tabs are called on the cards. */
	sourceLabel?: string | null;
	exampleLabel?: string | null;
}

const KIND_LABEL: Record<TapeMapKind, string> = {
	tab: "Tab in the new file",
	"example-tab": "Tab in the example file",
	"unused-tab": "Tab not used",
	table: "Standardized table",
	tape: "Loan tape",
	settings: "Tape settings",
	unmapped: "Unmapped column",
};

/** Legend order: the reading order of the picture. */
const LEGEND: readonly TapeMapKind[] = [
	"example-tab",
	"tab",
	"unused-tab",
	"table",
	"tape",
	"settings",
	"unmapped",
];

/** How the tables reach the tape, as far as the run says. */
const flowSentence = (map: TapeMap): string => {
	if (!map.hasSpine) {
		return "This run records no join between the tables, so each is drawn into the tape with the columns that read it.";
	}
	const spine = (map.spineLabel ?? "one of them").toLowerCase();
	return `The tables join onto the ${spine}, and the ${spine} becomes the tape.`;
};

const Facts: React.FC<{ rows: Array<[string, React.ReactNode]> }> = ({ rows }) => (
	<dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
		{rows.map(([term, value]) => (
			<React.Fragment key={term}>
				<dt className="text-text-muted">{term}</dt>
				<dd className="min-w-0 break-words text-text-base">{value}</dd>
			</React.Fragment>
		))}
	</dl>
);

const ColumnList: React.FC<{ title: string; columns: string[] }> = ({ title, columns }) =>
	columns.length > 0 ? (
		<div>
			<p className="text-xs font-semibold uppercase tracking-wider text-text-muted">
				{title}
			</p>
			<p className="mt-1 text-sm text-text-base">{columns.join(", ")}</p>
		</div>
	) : null;

interface PanelBodyProps {
	node: TapeMapNode | null;
	map: TapeMap;
	sourceName: string;
	exampleName: string;
}

/** What the side panel says about the selected card, or about the whole map. */
const PanelBody: React.FC<PanelBodyProps> = ({ node, map, sourceName, exampleName }) => {
	if (!node) {
		return (
			<>
				<p className="text-sm text-text-base">
					{map.hasExample
						? "Two files with differently named tabs fill the same standardized tables."
						: "The new file's tabs fill the standardized tables."}{" "}
					{flowSentence(map)}
				</p>
				<Facts
					rows={[
						["Schema", `${map.tableCount} tables · ${map.fieldCount} fields`],
						["New file", sourceName],
						...(map.hasExample
							? ([["Example file", exampleName]] as Array<[string, React.ReactNode]>)
							: []),
					]}
				/>
				<p className="text-xs text-text-muted">
					Select a card to see what it is. Drag a card to move it.
				</p>
			</>
		);
	}

	const { detail } = node;
	const table = detail.table ? tableLabel(detail.table) : null;

	if (node.kind === "table") {
		const showExample = Boolean(detail.exampleTab);
		return (
			<>
				<p className="text-sm text-text-base">
					{detail.grain ? `${detail.grain.charAt(0).toUpperCase()}${detail.grain.slice(1)}.` : ""}{" "}
					{detail.columns && detail.columns.length > 0
						? `${detail.columns.length} of the tape's columns read from it.`
						: "No tape column reads from it directly."}
				</p>
				<Facts
					rows={[
						["Schema name", <code className="font-mono text-xs">{detail.table}</code>],
						...(detail.grainKey
							? ([["Key", <code className="font-mono text-xs">{detail.grainKey}</code>]] as Array<[string, React.ReactNode]>)
							: []),
						["New file tab", detail.tab ?? "None"],
						...(map.hasExample
							? ([["Example file tab", detail.exampleTab ?? "None: this file has no such table"]] as Array<[string, React.ReactNode]>)
							: []),
						...(detail.joins && detail.joins.length > 0
							? ([
									[
										"Joins",
										<ul className="space-y-0.5">
											{detail.joins.map((join) => (
												<li key={join}>{join}</li>
											))}
										</ul>,
									],
								] as Array<[string, React.ReactNode]>)
							: []),
					]}
				/>
				{detail.fields && detail.fields.length > 0 && (
					<table className="w-full border-collapse text-[12.5px]">
						<thead>
							<tr className="border-b border-gray-200 text-left text-[10.5px] uppercase tracking-wider text-text-muted">
								<th className="py-1.5 pr-2 font-semibold">Field</th>
								<th className="py-1.5 pr-2 font-semibold">New file</th>
								{showExample && <th className="py-1.5 font-semibold">Example file</th>}
							</tr>
						</thead>
						<tbody>
							{detail.fields.map((field) => (
								<tr key={field.name} className="border-b border-gray-100 align-top">
									<td className="py-1.5 pr-2 font-mono text-[11.5px]">{field.name}</td>
									<td className={`py-1.5 pr-2 ${field.column ? "" : "text-text-muted"}`}>
										{field.column ?? "Not found"}
									</td>
									{showExample && (
										<td className={`py-1.5 ${field.exampleColumn ? "" : "text-text-muted"}`}>
											{field.exampleColumn ?? "Not found"}
										</td>
									)}
								</tr>
							))}
						</tbody>
					</table>
				)}
				{detail.fieldsComplete === false && (
					<p className="text-xs text-text-muted">
						This run names only the fields its tape reads: {detail.fields?.length ?? 0} of
						the table's {detail.fieldTotal}.
					</p>
				)}
				<ColumnList title="Tape columns" columns={detail.columns ?? []} />
			</>
		);
	}

	if (node.kind === "tab" || node.kind === "example-tab") {
		const isExample = node.kind === "example-tab";
		const other = isExample ? detail.tab : detail.exampleTab;
		const fields = detail.fields ?? [];
		// The new file's count is the run's own; the example file's is counted
		// from the fields, which a run that records that file lists in full.
		const filled = isExample
			? fields.filter((field) => field.exampleColumn).length
			: (detail.fieldsFilled ?? 0);
		const total = detail.fieldTotal ?? fields.length;
		return (
			<>
				<p className="text-sm text-text-base">
					Fills the standardized {table} table.{" "}
					{other
						? other === node.label
							? "The other file calls its tab the same thing."
							: `The other file calls the same thing “${other}”.`
						: map.hasExample
							? "The other file has no tab for this table."
							: ""}
				</p>
				<Facts
					rows={[
						["File", isExample ? exampleName : sourceName],
						["Fields filled", `${filled} of ${total}`],
					]}
				/>
				{!isExample && <ColumnList title="Tape columns" columns={detail.columns ?? []} />}
			</>
		);
	}

	if (node.kind === "unused-tab") {
		return (
			<>
				<p className="text-sm text-text-base">
					No standardized table reads this tab, so nothing in the tape comes from it.
				</p>
				<Facts rows={[["File", detail.file === "example" ? exampleName : sourceName]]} />
			</>
		);
	}

	if (node.kind === "tape") {
		const counts = detail.counts;
		return (
			<>
				<p className="text-sm text-text-base">
					One row per loan, built from the standardized tables once they are joined.
				</p>
				<Facts
					rows={[
						...(detail.loans !== null && detail.loans !== undefined
							? ([["Loans", detail.loans.toLocaleString()]] as Array<[string, React.ReactNode]>)
							: []),
						...(counts
							? ORIGINS.map(
									(origin) => [ORIGIN_LABEL[origin], String(counts[origin])] as [string, React.ReactNode],
								)
							: []),
					]}
				/>
			</>
		);
	}

	if (node.kind === "settings") {
		return (
			<>
				<p className="text-sm text-text-base">
					These columns are counted from the tape's own settings, such as its cutoff date, not read from a file.
				</p>
				<ColumnList title="Tape columns" columns={detail.columns ?? []} />
			</>
		);
	}

	return (
		<>
			<p className="text-sm text-text-base">
				The tape has this column, but the run recorded nothing in the mapping behind it. Check it before the tape goes out.
			</p>
			{table && <Facts rows={[["Table", table]]} />}
		</>
	);
};

const TapeMapGraph: React.FC<TapeMapGraphProps> = ({
	graph,
	columns,
	loans,
	sourceName,
	exampleName,
	sourceLabel,
	exampleLabel,
}) => {
	const map = useMemo(
		() => buildTapeMap({ graph, columns, loans, sourceLabel, exampleLabel }),
		[graph, columns, loans, sourceLabel, exampleLabel],
	);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [resetKey, setResetKey] = useState(0);
	const selected = map.nodes.find((node) => node.id === selectedId) ?? null;
	const present = new Set(map.nodes.map((node) => node.kind));
	const names = {
		sourceName: sourceName || "The new file",
		exampleName: exampleName || "The example file",
	};

	return (
		<div className="context-graph flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
			<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-gray-200 px-4 py-2.5">
				<h3 className="text-sm font-semibold text-text-base">Graph</h3>
				<p className="min-w-0 flex-1 text-[13px] text-[#5B6472]">
					Read left to right: raw tabs fill the standardized tables. {flowSentence(map)}
				</p>
				<button
					type="button"
					onClick={() => {
						setSelectedId(null);
						setResetKey((key) => key + 1);
					}}
					className="rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-[#004C4B] hover:bg-gray-50"
				>
					Reset view
				</button>
			</div>
			<div className="flex min-h-0 flex-col lg:flex-row">
				<div className="h-[68vh] min-h-[460px] min-w-0 flex-1">
					<Suspense fallback={<div className="h-full w-full" aria-busy="true" />}>
						<TapeMapCanvas
							map={map}
							selectedId={selected?.id ?? null}
							onSelect={setSelectedId}
							resetKey={resetKey}
						/>
					</Suspense>
				</div>
				<aside
					aria-live="polite"
					aria-label="About the selected card"
					className="flex w-full shrink-0 flex-col gap-3 overflow-y-auto border-t border-gray-200 px-4 py-3 lg:max-h-[68vh] lg:w-[300px] lg:border-l lg:border-t-0"
				>
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="text-xs uppercase tracking-wider text-text-muted">
								{selected ? KIND_LABEL[selected.kind] : "The whole map"}
							</p>
							<h3 className="mt-0.5 break-words text-base font-semibold text-text-base">
								{selected ? selected.label : map.hasExample ? "One schema, two files" : "One schema"}
							</h3>
						</div>
						{selected && (
							<button
								type="button"
								onClick={() => setSelectedId(null)}
								aria-label="Back to the whole map"
								className="rounded p-1 text-text-muted hover:bg-gray-100"
							>
								<X className="h-4 w-4" />
							</button>
						)}
					</div>
					<PanelBody node={selected} map={map} {...names} />
				</aside>
			</div>
			<div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-gray-200 px-4 py-2.5">
				{LEGEND.filter((kind) => present.has(kind)).map((kind) => (
					<span key={kind} className="flex items-center gap-1.5">
						<span
							aria-hidden="true"
							data-kind={kind}
							className="tape-map__card relative inline-block h-3.5 w-6 overflow-hidden rounded"
						>
							<span
								className="absolute inset-y-0 left-0 w-1"
								style={{ background: "var(--tm-accent)" }}
							/>
						</span>
						<span className="text-xs text-text-muted">
							{kind === "tab"
								? sourceLabel
									? `${sourceLabel} tab`
									: sourceName
										? `Tab in ${sourceName}`
										: KIND_LABEL[kind]
								: kind === "example-tab"
									? exampleLabel
										? `${exampleLabel} tab`
										: exampleName
											? `Tab in ${exampleName}`
											: KIND_LABEL[kind]
									: KIND_LABEL[kind]}
						</span>
					</span>
				))}
				<span className="flex items-center gap-1.5">
					<svg aria-hidden="true" width="24" height="6" viewBox="0 0 24 6">
						<line x1="0" y1="3" x2="24" y2="3" stroke="var(--cg-path)" strokeWidth="2.5" />
					</svg>
					<span className="text-xs font-medium" style={{ color: "var(--cg-path)" }}>
						this run's path
					</span>
				</span>
			</div>
		</div>
	);
};

export default TapeMapGraph;
